import React from 'react';
import { Lightbulb, Ruler } from 'lucide-react';
import { C } from '../../theme.js';
import { Alert, Eyebrow, FieldLabel, TextInput, Code } from '../../components/Primitives.jsx';
import { Segmented, ColumnsContext, OriginalColumnsContext } from '../StepInspector.jsx';
import { hasUpstreamSplit } from '../../data.js';
import { parseNumeric, resolveLiveRows, mergedColumns } from './columnClassification.js';
import { DecimalPlacesField } from '../shared/DecimalPlacesField.jsx';

// ── Skewness math (JS mirror of the backend's scipy.stats.skew, bias=True) ──
// Fisher-Pearson coefficient of skewness: g1 = m3 / m2^1.5, m_k = mean((x-mean)^k).
function computeSkewness(nums) {
  const vals = nums.filter(v => v !== null && v !== undefined && !isNaN(v));
  if (vals.length < 2) return null;
  const mean = vals.reduce((a, b) => a + b, 0) / vals.length;
  const m2 = vals.reduce((a, b) => a + (b - mean) ** 2, 0) / vals.length;
  const m3 = vals.reduce((a, b) => a + (b - mean) ** 3, 0) / vals.length;
  if (m2 === 0) return null;
  const g = m3 / Math.pow(m2, 1.5);
  return isFinite(g) ? g : null;
}

function classifySkew(gamma) {
  if (gamma === null || gamma === undefined) return 'N/A';
  const g = Math.abs(gamma);
  if (g < 0.5) return 'Symmetric';
  if (g < 1.0) return 'Moderately Skewed';
  return 'Highly Skewed';
}

function statusColor(status) {
  if (status === 'Symmetric') return '#10b981';
  if (status === 'Moderately Skewed') return '#f59e0b';
  if (status === 'Highly Skewed') return '#ef4444';
  if (status === 'Unparseable') return '#f43f5e';
  return C.fg5;
}

function parseVal(v) {
  const n = parseNumeric(v);
  return n === null ? NaN : n;
}

function getFitRows(rows) {
  if (!Array.isArray(rows) || !rows.length) return [];
  for (const key of Object.keys(rows[0] || {})) {
    const vals = new Set(rows.map(r => r[key]).filter(v => v !== null && v !== ''));
    if (vals.size > 0 && Array.from(vals).every(v => v === 'train' || v === 'test')) {
      const filtered = rows.filter(r => r[key] === 'train');
      if (filtered.length > 0) return filtered;
      break;
    }
  }
  return rows;
}

function applyTransform(n, method) {
  if (n === null || n === undefined || isNaN(n)) return null;
  if (method === 'sqrt') return n < 0 ? null : Math.sqrt(n);
  return n <= -1 ? null : Math.log1p(n);
}

export function SkewCorrectionInspector({ step, onChange, pipeline, uploadedRows }) {
  const c = step.config || {};
  const u = (patch) => onChange({ ...step, config: { ...c, ...patch } });
  const splitReady = pipeline ? hasUpstreamSplit(pipeline.steps, step.id) : true;
  const fitRows = React.useMemo(
    () => getFitRows(resolveLiveRows(uploadedRows, pipeline)),
    [uploadedRows, pipeline]
  );

  const liveColumns = React.useContext(ColumnsContext) || [];
  const origColumns = React.useContext(OriginalColumnsContext) || [];
  const allCols = mergedColumns(origColumns, liveColumns);
  const numericCols = React.useMemo(
    () => allCols.filter(col => col.type === 'number' && col.name !== '__rowIndex'),
    [allCols]
  );

  const method = c.method || 'log1p';
  const suffix = c.suffix || '_transformed';
  const column = c.column || '';

  // ── Skewness Diagnostic Table — every numeric column, train-only fit ────
  // `unparseable` distinguishes "had raw training values, but none of them
  // parsed as a number" (currency symbols, stray text) from a genuine
  // constant/zero-variance column — both would otherwise silently collapse
  // to the same "N/A" gamma with no indication of why.
  const diagnostics = React.useMemo(() => {
    return numericCols.map(col => {
      const raw = fitRows.map(r => r[col.name]).filter(v => v !== null && v !== undefined && v !== '');
      const nums = fitRows.map(r => parseVal(r[col.name]));
      const gamma = computeSkewness(nums);
      const unparseable = raw.length > 0 && nums.every(v => isNaN(v));
      return { column: col.name, gamma, status: unparseable ? 'Unparseable' : classifySkew(gamma), unparseable };
    });
  }, [numericCols, fitRows]);
  const unparseableCols = diagnostics.filter(d => d.unparseable);

  // ── Before / after preview for the currently selected column ────────────
  const preview = React.useMemo(() => {
    if (!column) return null;
    const beforeNums = fitRows.map(r => parseVal(r[column])).filter(v => !isNaN(v));
    if (!beforeNums.length) return null;
    const beforeGamma = computeSkewness(beforeNums);

    const hasNegative = beforeNums.some(n => n < 0);
    if (method === 'sqrt' && hasNegative) {
      return { beforeGamma, error: 'Negative values present in training data — Square Root requires non-negative input.' };
    }
    const hasInvalidLog = beforeNums.some(n => n <= -1);
    if (method === 'log1p' && hasInvalidLog) {
      return { beforeGamma, error: 'Values ≤ -1 present in training data — Log Transform (log1p) is undefined there.' };
    }

    const afterNums = beforeNums.map(n => applyTransform(n, method)).filter(v => v !== null);
    const afterGamma = computeSkewness(afterNums);
    return { beforeGamma, afterGamma, error: null };
  }, [column, method, fitRows]);

  return (
    <div>
      <Eyebrow>Distribution &amp; Skew Correction</Eyebrow>
      <div style={{ color: C.fg4, fontSize: '.78rem', margin: '4px 0 10px', lineHeight: 1.5 }}>
        Reduce right-skew in a numeric column using Square Root or a zero-safe Log Transform
        (<Code>log1p</Code>). A new <Code>{'<col>' + suffix}</Code> column is added; the source
        column is kept unchanged.
      </div>

      {!splitReady && (
        <Alert kind="warn" style={{ marginBottom: 10 }}>
          No Train / Test Split step found upstream — the skewness diagnostic and transform
          validation use all rows. If the result feeds a model, add a Train / Test Split step
          first to avoid data leakage.
        </Alert>
      )}

      {unparseableCols.length > 0 && (
        <Alert kind="warn" style={{ marginBottom: 10 }}>
          {unparseableCols.map(d => d.column).join(', ')} had no parseable numeric values in the
          training data (check for currency symbols, commas, or stray text) — skewness shows as
          N/A rather than a misleading number.
        </Alert>
      )}

      {/* Skewness Diagnostic Table */}
      <div style={{
        marginBottom: 14, background: C.surface, border: `1px solid ${C.border}`,
        borderRadius: 8, overflow: 'hidden'
      }}>
        <div style={{
          padding: '8px 12px', background: 'var(--info-bg)', borderBottom: `1px solid ${C.accentMuted}`,
          fontSize: '.76rem', fontWeight: 700, color: C.accentBright
        }}>
          <Ruler size={12} style={{ display: 'inline', verticalAlign: -1, marginRight: 5 }} /> Skewness Diagnostic — all numeric columns
        </div>
        <div style={{ maxHeight: 220, overflowY: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.76rem', textAlign: 'left' }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${C.borderSubtle}`, color: C.fg4 }}>
                <th style={{ padding: '6px 10px', fontWeight: 600 }}>Column</th>
                <th style={{ padding: '6px 10px', fontWeight: 600 }}>Skewness (γ)</th>
                <th style={{ padding: '6px 10px', fontWeight: 600, textAlign: 'right' }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {diagnostics.length === 0 ? (
                <tr>
                  <td colSpan={3} style={{ padding: 12, textAlign: 'center', color: C.fg5 }}>
                    No numeric columns found in dataset schema.
                  </td>
                </tr>
              ) : (
                diagnostics.map(d => (
                  <tr
                    key={d.column}
                    onClick={() => u({ column: d.column })}
                    style={{
                      borderBottom: `1px solid ${C.borderSubtle}`,
                      cursor: 'pointer',
                      background: d.column === column ? 'var(--info-bg)' : 'transparent'
                    }}
                  >
                    <td style={{ padding: '6px 10px', color: C.fg1, fontWeight: 500 }}>{d.column}</td>
                    <td style={{ padding: '6px 10px', color: C.fg2, fontFamily: "'JetBrains Mono', monospace" }}>
                      {d.gamma === null ? 'N/A' : d.gamma.toFixed(4)}
                    </td>
                    <td style={{ padding: '6px 10px', textAlign: 'right' }}>
                      <span style={{
                        fontSize: '.7rem', fontWeight: 700, color: statusColor(d.status),
                        padding: '2px 8px', borderRadius: 4,
                        background: `${statusColor(d.status)}22`
                      }}>
                        {d.status}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Transformation Configuration Panel */}
      <div style={{ marginTop: 10 }}>
        <FieldLabel hint="The numeric column to correct for skew">Column</FieldLabel>
        <select
          value={column}
          onChange={(e) => u({ column: e.target.value })}
          style={{
            width: '100%', background: C.input, color: C.fg1,
            border: `1px solid ${C.borderStrong}`, borderRadius: 6,
            padding: '6px 9px', fontSize: '.82rem', outline: 'none',
            fontFamily: 'inherit', marginTop: 4, cursor: 'pointer'
          }}
        >
          <option value="" disabled>Select a numeric column…</option>
          {numericCols.map(col => (
            <option key={col.name} value={col.name}>{col.name}</option>
          ))}
        </select>
      </div>

      <div style={{ marginTop: 10 }}>
        <FieldLabel hint="Square Root requires non-negative input; Log Transform (log1p) is zero-safe">
          Transformation Method
        </FieldLabel>
        <Segmented
          value={method}
          onChange={(v) => u({ method: v })}
          options={[
            { value: 'sqrt', label: 'Square Root (sqrt)' },
            { value: 'log1p', label: 'Log Transform (log1p)' }
          ]}
        />
      </div>

      <div style={{ marginTop: 10 }}>
        <FieldLabel hint="Appended to the source column name for the output column, e.g. col_transformed">
          Output Column Suffix
        </FieldLabel>
        <TextInput value={suffix} onChange={(v) => u({ suffix: v })} placeholder="_transformed" />
      </div>

      <div style={{ marginTop: 10 }}>
        <FieldLabel hint="Optional full output column name override. Defaults to <col><suffix>">
          Output Column Name (Optional)
        </FieldLabel>
        <TextInput value={c.output_column || ''} onChange={(v) => u({ output_column: v })} placeholder={`Auto (e.g., ${column || 'col'}${suffix})`} />
      </div>

      {/* Live Preview — before/after skew comparison */}
      {column && (
        preview ? (
          preview.error ? (
            <Alert kind="error" style={{ marginTop: 14 }}>{preview.error}</Alert>
          ) : (
            <div style={{
              marginTop: 14, padding: '10px 12px',
              background: 'var(--info-bg)', border: `1px solid ${C.accentMuted}`,
              borderRadius: 8, fontSize: '.76rem', color: C.fg1, lineHeight: 1.5
            }}>
              <Lightbulb size={13} style={{ display: 'inline', verticalAlign: -2, marginRight: 4 }} /> <strong>Preview:</strong> Skewness for <Code>{column}</Code> goes from{' '}
              <strong>{preview.beforeGamma === null ? 'N/A' : preview.beforeGamma.toFixed(4)}</strong>
              {' '}({classifySkew(preview.beforeGamma)}) to{' '}
              <strong>{preview.afterGamma === null ? 'N/A' : preview.afterGamma.toFixed(4)}</strong>
              {' '}({classifySkew(preview.afterGamma)}) after {method === 'sqrt' ? 'Square Root' : 'Log Transform'}.
            </div>
          )
        ) : (
          <div style={{
            marginTop: 14, padding: 12, borderRadius: 8, background: C.surface,
            border: `1px dashed ${C.border}`, color: C.fg4, fontSize: '.76rem', textAlign: 'center'
          }}>
            No data available yet to preview this column&apos;s skew.
          </div>
        )
      )}
      <DecimalPlacesField value={c.output_decimals} onChange={(v) => u({ output_decimals: v })} />
    </div>
  );
}
